import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useScenarios } from '../../hooks/useScenarios';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { useApp } from '../../contexts/AppContext';

import { DependencyType } from '../../types/scenarios';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';

interface CreateScenarioModalProps {
  open: boolean;
  onClose: () => void;
  onScenarioCreated?: (scenarioId: string) => void;
  initialTemplate?: 'growth' | 'cost-cutting' | 'crisis';
}

const TEMPLATE_DEFAULTS: Record<'growth' | 'cost-cutting' | 'crisis', { name: string; description: string; category: 'growth' | 'cost-cutting' | 'crisis' }> = {
  growth: {
    name: 'Growth Scenario',
    description: 'Model new customer wins and revenue growth',
    category: 'growth',
  },
  'cost-cutting': {
    name: 'Cost Reduction Plan',
    description: 'Explore cost-cutting opportunities',
    category: 'cost-cutting',
  },
  crisis: {
    name: 'Crisis Plan',
    description: 'Prepare for challenging situations',
    category: 'crisis',
  },
};

export const CreateScenarioModal: React.FC<CreateScenarioModalProps> = ({ open, onClose, onScenarioCreated, initialTemplate }) => {
  const { createScenario } = useScenarios();
  const allScenarios = useVisibleScenarios();
  const { currentUser } = useApp();
  const currentUserName = currentUser.name;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'growth' | 'cost-cutting' | 'crisis' | undefined>(undefined);
  const [startingPoint, setStartingPoint] = useState<'base' | 'blank'>('base');
  const [depsExpanded, setDepsExpanded] = useState(false);
  const [selectedDependencies, setSelectedDependencies] = useState<{targetId: string; type: DependencyType}[]>([]);
  const [depTargetId, setDepTargetId] = useState<string>('');
  const [depType, setDepType] = useState<DependencyType>('prerequisite');

  useEffect(() => {
    if (open && initialTemplate) {
      const defaults = TEMPLATE_DEFAULTS[initialTemplate];
      if (defaults) {
        setName(defaults.name);
        setDescription(defaults.description);
        setCategory(defaults.category);
      }
    }
  }, [open, initialTemplate]);

  const handleCreate = async () => {
    if (!name) {
      return;
    }

    // Create the scenario with appropriate changes based on starting point
    // Base: Empty changes array (will be populated from base forecast in builder)
    // Blank: Empty changes array (user builds from scratch)
    const changes: any[] = [];
    
    const newScenarioId = await createScenario({
      name,
      description,
      status: 'draft',
      priority: 'medium',
      label: null,
      category: category || undefined,
      createdBy: currentUser.id,
      createdByName: currentUserName,
      modifiedBy: currentUserName,
      shares: [],
      isFavorite: false,
      changes,
      commentCount: 0,
      currentVersion: 1,
      collectionIds: [],
      metrics: {
        minCash: 0,
        minCashDate: new Date(),
        runway: 0,
        revenueChange: 0,
        revenueChangeAmount: 0,
        costChange: 0,
        costChangeAmount: 0,
        netImpact: 0,
        endingCash: 0,
      },
      tags: [],
      // Store starting point preference for builder to use
      startingPoint,
      // Embed dependencies inside the scenario object so they travel as a single param
      // Always pass the array (even if empty) so the context can see the actual value
      _dependencies: selectedDependencies,
    });

    // Close modal first
    onClose();

    // Navigate to the scenario detail screen after a brief delay to ensure state updates
    setTimeout(() => {
      if (onScenarioCreated) {
        onScenarioCreated(newScenarioId);
      }
    }, 100);

    // Reset form
    setName('');
    setDescription('');
    setCategory(undefined);
    setStartingPoint('base');
    setSelectedDependencies([]);
    setDepsExpanded(false);
    setDepTargetId('');
    setDepType('prerequisite');
  };

  const isCreateDisabled = !name;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Scenario</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="name">Scenario Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Q1 2026 Growth Plan"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="Describe this scenario..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label>Starting Point *</Label>
            <RadioGroup value={startingPoint} onValueChange={(v: string) => setStartingPoint(v as 'base' | 'blank')} className="mt-2 space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="base" id="base" />
                <Label htmlFor="base" className="font-normal cursor-pointer">
                  Base from Base Forecast <span className="text-muted-foreground">(recommended)</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 opacity-40 cursor-not-allowed">
                <RadioGroupItem value="blank" id="blank" disabled />
                <Label htmlFor="blank" className="font-normal cursor-not-allowed select-none">
                  Start Blank <span className="text-muted-foreground">(coming soon)</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Dependencies (optional, collapsible) */}
          <div className="border rounded-lg">
            <button
              type="button"
              onClick={() => setDepsExpanded(!depsExpanded)}
              className="flex items-center justify-between w-full p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>Dependencies (optional){selectedDependencies.length > 0 && ` — ${selectedDependencies.length} selected`}</span>
              {depsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {depsExpanded && (
              <div className="p-3 pt-0 space-y-3">
                <div className="flex gap-2">
                  <Select value={depType} onValueChange={(v) => setDepType(v as DependencyType)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prerequisite">Prerequisite</SelectItem>
                      <SelectItem value="conflict">Conflict</SelectItem>
                      <SelectItem value="enables">Enables</SelectItem>
                      <SelectItem value="enhanced-by">Enhanced By</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={depTargetId} onValueChange={setDepTargetId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select scenario..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allScenarios
                        .filter(s => !selectedDependencies.some(d => d.targetId === s.id))
                        .map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={!depTargetId}
                    onClick={() => {
                      if (depTargetId) {
                        setSelectedDependencies(prev => [...prev, { targetId: depTargetId, type: depType }]);
                        setDepTargetId('');
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {selectedDependencies.length > 0 && (
                  <div className="space-y-1">
                    {selectedDependencies.map((dep, idx) => {
                      const target = allScenarios.find(s => s.id === dep.targetId);
                      return (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <span>
                            <Badge variant="outline" className="mr-2">{dep.type}</Badge>
                            {target?.name ?? dep.targetId}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setSelectedDependencies(prev => prev.filter((_, i) => i !== idx))}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreateDisabled}>
            Create Scenario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
